import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_IMG = "/photos/hero-cuarto-bolero.webp";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
    <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="inicio" className="relative min-h-screen overflow-hidden flex flex-col justify-center">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Cuarto Bolero en directo"
          className="h-full w-full object-cover"
          initial={{ scale: 1.12, opacity: 0.35 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.2, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/45 to-ink" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,30,45,0.16),transparent_60%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 sm:px-12 lg:px-24 pt-24 pb-8">
        <motion.p className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}>
          Trío de bolero · Son cubano · Desde 1997
        </motion.p>

        <h1 className="mt-6 font-sans text-cream leading-[0.95] tracking-tight text-[clamp(3.2rem,11vw,10rem)] drop-shadow-[0_6px_30px_rgba(0,0,0,0.65)]" data-testid="hero-title">
          <Line delay={0.15}>
            <span className="font-light text-gold">CUARTO</span>
          </Line>
          <Line delay={0.32}>
            <span className="font-semibold">BOLERO</span>
          </Line>
        </h1>

        <motion.p
          className="mt-6 max-w-xl font-serif italic text-xl sm:text-2xl text-cream/85"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          data-testid="hero-tagline"
        >
          “Boleros, emoción y música en directo.”
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
        >
          <button
            data-testid="hero-listen-button"
            onClick={() => scrollToId("musica")}
            className="rounded-full bg-gold px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
          >
            Escuchar
          </button>
          <button
            data-testid="hero-concerts-button"
            onClick={() => scrollToId("conciertos")}
            className="rounded-full border border-cream/30 px-8 py-3.5 text-[13px] uppercase tracking-[0.18em] text-cream transition-all duration-300 hover:border-gold hover:text-gold"
          >
            Próximos conciertos
          </button>
          <button
            data-testid="hero-booking-button"
            onClick={() => scrollToId("contratacion")}
            className="group inline-flex items-center gap-2 px-2 py-3.5 text-[13px] uppercase tracking-[0.18em] text-sand hover:text-gold transition-colors"
          >
            Contratación
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-0 inset-x-0 z-10 mx-auto w-full max-w-[1600px] px-6 sm:px-12 lg:px-24 pb-8 flex items-end justify-between">
        <p className="text-[11px] uppercase tracking-[0.3em] text-mute">Música en directo · España</p>
        <motion.div className="hidden sm:flex flex-col items-center gap-3 text-mute" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
          <span className="text-[10px] uppercase tracking-[0.3em]">Desliza</span>
          <ArrowDown size={14} />
        </motion.div>
        <p className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-mute">Casi tres décadas</p>
      </motion.div>
    </section>
  );
};
